/**
 * DPoP request interceptor. A thin `fetch` wrapper that attaches a proof-of-possession
 * header signed by this browser's device key (see {@link makeProof} in ./deviceKey).
 *
 * It lives in the styleguide, not the shell, because the mutating chokepoints that need
 * it are spread across packages a pilet can reach: the play-token call (watch pilet),
 * device enrollment (shell) and device removal (profile pilet). Pilets depend on the
 * styleguide, not on each other or the shell, so a cross-package helper has nowhere else
 * to live — the same rationale as ./storage.ts.
 *
 * Reserved for those chokepoints; ordinary reads keep using plain `fetch`. The CSRF token
 * is unaffected — callers still put `__csrfToken` in the body as before; this only adds
 * the `DPoP` header alongside it.
 */
import { makeProof } from './deviceKey'

/**
 * The `htu` claim of a DPoP proof: the request's absolute target URI with the query and
 * fragment stripped (RFC 9449). Root-relative inputs (`/api/...`) are resolved against
 * the current origin. The `fetch` itself still uses the original input.
 */
function proofTarget (input: string): string {
  const url = new URL(input, window.location.origin)
  return `${url.origin}${url.pathname}`
}

/**
 * The most recent nonce the server has handed out via a `DPoP-Nonce` response header.
 * A server that requires nonces rotates them by returning a fresh one on ANY response
 * (2xx or 401), so we cache the latest and attach it proactively on the next request —
 * that spares the recurring calls (the heartbeats) a guaranteed 401 round-trip each time.
 *
 * One shared cache is enough: every chokepoint is same-origin, and if a proactively-sent
 * nonce is ever stale or meant for a different sub-service the server simply answers 401
 * with a new one and the retry below recovers. Concurrent requests race harmlessly here —
 * last write wins, and a wrong guess self-corrects on the next response.
 */
let cachedNonce: string | null = null

/**
 * `fetch` with a DPoP proof header bound to this request's method (`htm`) and target
 * (`htu`), carrying the cached server nonce when we have one. If the server answers
 * **401 with a `DPoP-Nonce` header** — its way of demanding a fresh, server-chosen nonce
 * inside the proof — the request is retried ONCE with a new proof carrying that nonce.
 * Any other response (including a 401 without the nonce header, i.e. a genuine auth
 * failure) is returned to the caller untouched.
 */
export async function dpopFetch (input: string, init: RequestInit = {}): Promise<Response> {
  const htm = (init.method ?? 'GET').toUpperCase()
  const htu = proofTarget(input)

  const send = async (nonce: string | null): Promise<Response> => {
    const proof = await makeProof({ htm, htu, nonce: nonce ?? undefined })
    const headers = new Headers(init.headers)
    headers.set('DPoP', proof)
    const res = await fetch(input, { ...init, headers })
    // Adopt whatever nonce this response offers so the next request leads with it.
    const offered = res.headers.get('DPoP-Nonce')
    if (offered) cachedNonce = offered
    return res
  }

  const res = await send(cachedNonce)
  // A 401 that supplied a fresh nonce is the server asking us to prove again with it.
  // Retry with the nonce *this* response offered, not with the shared cache: two
  // requests in flight at once (a play-token while a progress report is out, say)
  // both write that cache, so re-reading it here can retry with the other request's
  // nonce — and a server that issues single-use nonces then rejects this one too,
  // with no retry left. The header is the answer to this request; use it directly.
  const fresh = res.status === 401 ? res.headers.get('DPoP-Nonce') : null
  if (fresh) return send(fresh)
  return res
}
